import { useCallback, useLayoutEffect, useRef } from 'react';

type VisibleReply = {
	id: string;
};

type ReplyFocusRestoration = {
	getReplyContainer: (replyId: string) => HTMLDivElement | undefined;
	requestFocusOnRevealedReply: (replyId: string) => void;
	requestFocusOnShowReplies: () => void;
	setReplyContainerRef: (replyId: string, replyContainer: HTMLDivElement | null) => void;
	showRepliesButtonRef: (button: HTMLButtonElement | null) => void;
};

export const useReplyFocusRestoration = (
	visibleReplies: readonly VisibleReply[] | undefined,
): ReplyFocusRestoration => {
	const shouldFocusShowRepliesButtonRef = useRef(false);
	const replyToFocusAfterExpansionRef = useRef<string | null>(null);
	const replyContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

	const showRepliesButtonRef = useCallback((button: HTMLButtonElement | null) => {
		if (button && shouldFocusShowRepliesButtonRef.current) {
			shouldFocusShowRepliesButtonRef.current = false;
			button.focus();
		}
	}, []);

	const requestFocusOnShowReplies = useCallback(() => {
		shouldFocusShowRepliesButtonRef.current = true;
	}, []);

	const setReplyContainerRef = useCallback(
		(replyId: string, replyContainer: HTMLDivElement | null) => {
			if (replyContainer) {
				replyContainerRefs.current.set(replyId, replyContainer);
			} else {
				replyContainerRefs.current.delete(replyId);
			}
		},
		[],
	);

	const getReplyContainer = useCallback(
		(replyId: string) => replyContainerRefs.current.get(replyId),
		[],
	);

	const requestFocusOnRevealedReply = useCallback((replyId: string) => {
		replyToFocusAfterExpansionRef.current = replyId;
	}, []);

	useLayoutEffect(() => {
		const replyId = replyToFocusAfterExpansionRef.current;
		if (!replyId || !visibleReplies?.some((reply) => reply.id === replyId)) {
			return;
		}

		const replyContainer = replyContainerRefs.current.get(replyId);
		if (!replyContainer) {
			return;
		}

		replyToFocusAfterExpansionRef.current = null;
		// Make the newly rendered reply focusable before moving focus.
		replyContainer.tabIndex = 0;
		replyContainer.focus();
	}, [visibleReplies]);

	return {
		getReplyContainer,
		requestFocusOnRevealedReply,
		requestFocusOnShowReplies,
		setReplyContainerRef,
		showRepliesButtonRef,
	};
};
